import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import {
  Forest,
  GrainOutlined,
  InfoOutlined,
  ReportProblemOutlined,
  WaterDropOutlined,
} from "@mui/icons-material";
import sproutIcon from "../../assets/icons/lucide-sprout-icon.svg";
import PropTypes from "prop-types";

const FertilizerRecommendationCard = ({
  recommendations,
  cropName,
  loadFertilizerRecommendations,
  reportId,
  targetYield,
  setTargetYield,
  isRecLoading,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [targetYieldInput, setTargetYieldInput] = useState(targetYield);

  const changeYieldTarget = () => {
    setTargetYield(targetYieldInput);
    loadFertilizerRecommendations(reportId, targetYieldInput); // Pass new value
    setOpen(false);
  };

  const getIcon = (name) => {
    switch (name.toLowerCase()) {
      case "urea":
        return (
          <Forest
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "green",
              fontWeight: "500",
            }}
          />
        );
      case "dap (diammonium phosphate)":
        return (
          <WaterDropOutlined
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "blue",
              fontWeight: "500",
            }}
          />
        );
      case "mop (muriate of potash)":
        return (
          <GrainOutlined
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "orange",
              fontWeight: "500",
            }}
          />
        );
      case "biochar compost":
        return <img src={sproutIcon} alt="My Icon" />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Card
        sx={{
          margin: "auto",
          marginTop: 2,
          borderRadius: 2,
          boxShadow: 3,
          opacity: isRecLoading ? 0.6 : 1,
          pointerEvents: isRecLoading ? "none" : "auto",
        }}
      >
        <CardContent>
          <Typography variant="h6" component="div" gutterBottom>
            {t("Fertilizer Recommendations")}
          </Typography>
          {isNotNullOrEmptyArray(recommendations) ? (
            <>
              <List disablePadding>
                {recommendations.map((recommendation, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="center" className="py-3">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "1rem",
                        }}
                      >
                        {getIcon(recommendation.fertilizer_name)}
                      </Box>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              color: "black",
                            }}
                          >
                            {recommendation.fertilizer_name}
                          </Typography>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "gray",
                                fontWeight: "500",
                                fontSize: "0.75rem",
                                marginTop: "4px",
                              }}
                            >
                              {recommendation.description}
                            </Typography>
                            <Typography
                              sx={{
                                display: "inline",
                                fontWeight: "500",
                                marginTop: "0.5rem",
                                fontSize: "0.75rem",
                              }}
                              component="span"
                              variant="body2"
                              color="text.primary"
                            >
                              {`Recommended: ${recommendation.quantity} ${recommendation.unit}`}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              {["wheat", "sugarcane"].includes(
                cropName?.toLowerCase().trim()
              ) && (
                <>
                  <Card
                    sx={{
                      my: 3,
                      p: 3,
                      bgcolor: "#E8F5E9",
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {targetYield ? (
                          <>
                            {t(
                              "These are the fertilizer recommendations for the target yield of"
                            )}
                            &nbsp;
                            <Box
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: "#1B5E20",
                              }}
                            >
                              {targetYield} {t("tons/hectare")}
                            </Box>
                          </>
                        ) : (
                          t(
                            "Enter a target yield to get customized fertilizer recommendations"
                          )
                        )}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() => {
                          setTargetYieldInput(targetYield);
                          setOpen(true);
                        }}
                        sx={{
                          mt: 2,
                          bgcolor: "#4CAF50",
                          color: "white",
                          "&:hover": { bgcolor: "#45a049" },
                        }}
                      >
                        {targetYield
                          ? t("Change Target Yield")
                          : t("Set Target Yield")}
                      </Button>
                    </Box>
                  </Card>

                  {/* Dialog */}
                  <Dialog
                    open={open}
                    onClose={() => setOpen(false)}
                    maxWidth="xs"
                    fullWidth
                  >
                    <DialogTitle>{t("Set Target Yield")}</DialogTitle>
                    <DialogContent sx={{ py: 2 }}>
                      <TextField
                        autoFocus
                        margin="dense"
                        label={t("Target Yield (in tons/hectare)")}
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={targetYieldInput}
                        onChange={(e) => setTargetYieldInput(e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ mt: 1, mb: 2 }}
                        disabled={isRecLoading}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setOpen(false)}>
                        {t("Cancel")}
                      </Button>
                      <Button
                        onClick={changeYieldTarget}
                        variant="contained"
                        sx={{
                          bgcolor: "#4CAF50",
                          color: "white",
                          "&:hover": { bgcolor: "#45a049" },
                        }}
                      >
                        {t("Apply")}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
              <Alert
                severity="warning"
                icon={
                  <ReportProblemOutlined
                    sx={{
                      width: "1.5rem",
                      height: "1.5rem",
                    }}
                  />
                }
                sx={{
                  marginTop: "24px",
                  textAlign: "left",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption">
                  <b>{t("Disclaimer")}:</b>{" "}
                  {t(`These recommendations are based on general
            guidelines. Always consult with a local agricultural expert for
            personalized advice.`)}
                </Typography>
              </Alert>
            </>
          ) : (
            <Alert
              severity="info"
              icon={
                <InfoOutlined
                  style={{
                    width: "1.5rem",
                    height: "1.5rem",
                  }}
                />
              }
              sx={{
                marginTop: "24px",
                textAlign: "left",
              }}
            >
              <Typography variant="body2">
                {t(`No fertilizer recommendations found. This could be due to insufficient
          data or soil conditions that don't require additional fertilizers at
          this time.`)}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
      {isRecLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.5)",
            borderRadius: 2,
            zIndex: 10,
          }}
        >
          <CircularProgress size={48} sx={{ color: "#4CAF50" }} />
        </Box>
      )}
    </Box>
  );
};

FertilizerRecommendationCard.propTypes = {
  recommendations: PropTypes.arrayOf(
    PropTypes.shape({
      fertilizer_name: PropTypes.string,
      description: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      unit: PropTypes.string,
    })
  ),
  cropName: PropTypes.string,
  loadFertilizerRecommendations: PropTypes.func,
  reportId: PropTypes.number,
  targetYield: PropTypes.string,
  setTargetYield: PropTypes.func,
  isRecLoading: PropTypes.bool,
};

export default FertilizerRecommendationCard;
